import { CreditProductsApi } from '~/api';
import { action, makeObservable, observable, persist } from '~/modules';
import {
  CREDIT_PRODUCT_TYPE,
  CREDIT_SIMULATION_METHOD,
  type DOC_TYPES,
  PAYMENT_TYPE,
  uploadFileToAzure,
} from '~/utils';

const emptyProductDetails = {
  name: '',
  type: '',
  status: null,
  cardNumber: '',
  bestDayToBuy: '',
  totalLimit: 0,
  usedLimit: 0,
  availableLimit: 0,
  paymentType: null,
};

export default class CreditProductsStore {
  constructor() {
    makeObservable(this);
  }

  @persist('object')
  @observable
  qrCodeId: string | null = null;

  @persist('object')
  @observable
  products: CreditProductsResponse[] = [];

  @observable
  productLoanDetails: CreditProductsDetails = emptyProductDetails;

  @observable
  currentCreditSimulationRequest: CreditSimulationRequest | null = null;

  @persist('object')
  @observable
  pendingWithdrawal: Withdrawal | null = null;

  @persist('object')
  @observable
  requestedCreditSimulationValue = 0;

  @persist('object')
  @observable
  simulationPlans: SimulationPlans[] = [];

  @persist('object')
  @observable
  currentSimulationPlan: SimulationPlans | null = null;

  @persist('object')
  @observable
  currentCreditSimulationMethod: CREDIT_SIMULATION_METHOD | null = null;

  get loanInfo(): CreditProductsResponse | undefined {
    return this.products?.filter((pdt) => {
      return pdt.type === CREDIT_PRODUCT_TYPE.LOAN;
    })[0];
  }

  @action
  setQrCodeId = (id: string) => {
    this.qrCodeId = id;
  };

  @action
  setRequestedCreditSimulationValue = (value: number) => {
    this.requestedCreditSimulationValue = value;
  };

  @action
  setCurrentCreditSimulationRequest = (data: CreditSimulationRequest) => {
    this.currentCreditSimulationRequest = {
      ...this.currentCreditSimulationRequest,
      ...data,
    };
  };

  @action
  setSimulationPlans = (plans: SimulationPlans[]) => {
    this.simulationPlans = plans;
  };

  @action
  setCurrentSimulationPlan = (plan: SimulationPlans) => {
    this.currentSimulationPlan = plan;
  };

  @action
  setProducts = (products: CreditProductsResponse[]) => {
    this.products = products;
  };

  @action
  setProductLoanDetails = (details: CreditProductsDetails) => {
    this.productLoanDetails = details;
  };

  @action
  setPendingWithdrawal = (withdrawal: Withdrawal) => {
    this.pendingWithdrawal = withdrawal;
  };

  @action
  setCurrentCreditSimulationMethod = (method: CREDIT_SIMULATION_METHOD) => {
    this.currentCreditSimulationMethod = method;
  };

  private validateLoanProduct(): void {
    if (!this.loanInfo) {
      throw new Error('Nenhum produto de empréstimo encontrado');
    }
  }

  @action
  getProducts = async () => {
    const data = await CreditProductsApi.getProducts();
    this.setProducts(data);
  };

  @action
  getBanks = async () => {
    return await CreditProductsApi.getBanks();
  };

  @action
  getProductLoanDetails = async () => {
    this.validateLoanProduct();
    const id = this.loanInfo!.id;
    const data = await CreditProductsApi.getProductsDetails(id);
    this.setProductLoanDetails(data);
  };

  @action
  getSimulationPlans = async (amount: number) => {
    this.validateLoanProduct();
    const id = this.loanInfo!.id;
    const data = await CreditProductsApi.getSimulationPlans(amount, id);
    this.setRequestedCreditSimulationValue(data.requestedAmount);
    this.setSimulationPlans(data.plans);
  };

  @action
  creditSimulation = async (): Promise<void> => {
    const id = this.loanInfo?.id || '';
    const payload = {
      planId: this.currentSimulationPlan?.id,
      amount: this.requestedCreditSimulationValue,
      paymentType:
        this.currentCreditSimulationRequest?.paymentType ||
        PAYMENT_TYPE.TRANSFER,
      numberOfInstallments: this.currentSimulationPlan?.numberOfInstallments,
      flow: 'QR_CODE',
      pixKey: this.currentCreditSimulationRequest?.pixKey || undefined,
      bankNumber: this.currentCreditSimulationRequest?.bankNumber || undefined,
      agency: this.currentCreditSimulationRequest?.agency || undefined,
      account: this.currentCreditSimulationRequest?.account || undefined,
      agencyDigit:
        this.currentCreditSimulationRequest?.agencyDigit || undefined,
      accountDigit:
        this.currentCreditSimulationRequest?.accountDigit || undefined,
    };
    await CreditProductsApi.creditSimulation(payload, id);
  };

  @action
  cancelCreditSimulation = async (): Promise<void> => {
    await CreditProductsApi.cancelCreditSimulation();
  };

  @action
  getPendingWithdraw = async (): Promise<void> => {
    const data = await CreditProductsApi.getPendingWithdraw();
    this.setPendingWithdrawal(data);
  };

  @action
  confirmWithdrawal = async (): Promise<void> => {
    await CreditProductsApi.confirmWithdrawal(
      this.pendingWithdrawal?.preAuthorizationId,
    );
  };

  @action
  creditProductsImageValidation = async (file: {
    uri: string;
    name: string;
    mimeType: string;
    blob: Blob;
    type: DOC_TYPES;
  }): Promise<void> => {
    await uploadFileToAzure({
      blob: file.blob,
      name: file.name,
      type: file.mimeType,
    });
    const id = this.loanInfo!.id;
    await CreditProductsApi.creditProductsImageValidation(
      {
        filename: file.name,
        type: file.type,
        contentType: file.mimeType,
      },
      id,
    );
  };
}
